import { useEffect, useState } from "react";
import { api, type PlanInfo, type PlanDef } from "../../lib/api";

// Feature bullets for a plan card, derived from its metrics + capabilities.
function planFeatures(plan: PlanDef): string[] {
  const feats: string[] = [];
  feats.push(
    plan.runsPerMonth === -1
      ? "Unlimited generation runs"
      : `${plan.runsPerMonth} generation runs / mo`,
  );
  feats.push(
    plan.brands === -1
      ? "Unlimited brands"
      : `${plan.brands} brand${plan.brands === 1 ? "" : "s"}`,
  );
  feats.push(
    plan.capabilities.publish
      ? "Connect accounts, schedule & auto-publish"
      : "Generate & review only",
  );
  if (plan.capabilities.analytics) feats.push("Analytics dashboards");
  if (plan.teamSeats > 0) feats.push(`${plan.teamSeats} team seats`);
  return feats;
}

export default function Billing() {
  const [current, setCurrent] = useState<PlanInfo | null>(null);
  const [plans, setPlans] = useState<PlanDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refreshPlans = () =>
    api.billing.plans().then((r) => {
      setCurrent(r.current);
      setPlans(r.plans);
    });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromPortal = params.get("from_portal") === "1";

    const load = async () => {
      if (fromPortal) {
        try {
          await api.billing.sync();
        } catch {
          /* best-effort */
        }
        window.history.replaceState({}, "", window.location.pathname);
      }
      await refreshPlans();
    };

    load()
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async (planId: string) => {
    setSubscribing(planId);
    try {
      const { url } = await api.billing.checkout(planId);
      window.location.assign(url);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to start checkout");
      setSubscribing(null);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await api.billing.portal();
      window.location.assign(url);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to open billing portal");
      setPortalLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.billing.sync();
      await refreshPlans();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <p className="text-faint text-sm">Loading…</p>;

  const currentPlan = current?.plan ?? "free";
  const onFree = currentPlan === "free";

  return (
    <div>
      <div className="mb-8">
        <h1 className="page-title">Billing</h1>
        <p className="text-sm text-muted mt-1">
          {onFree
            ? "You're on the Free plan. Upgrade to publish and scale up."
            : "Manage your subscription and see what each plan includes."}
        </p>
      </div>

      {/* ── Active subscription management (paid plans) ─────────────────────── */}
      {!onFree && (
        <div className="card p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="eyebrow mb-1">Current plan</p>
              <p className="text-2xl font-extrabold text-ink capitalize">
                {currentPlan}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {current?.status === "active" && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    Active
                  </span>
                )}
                {current?.status === "past_due" && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                    Payment past due
                  </span>
                )}
                <span className="text-xs bg-gray-100 text-slate px-2 py-0.5 rounded-full font-medium">
                  {current?.interval === "year"
                    ? "Annual billing"
                    : "Monthly billing"}
                </span>
              </div>
              {current?.periodEnd && (
                <p className="text-xs text-faint mt-2">
                  Next billing date:{" "}
                  {new Date(current.periodEnd).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <button
                onClick={handlePortal}
                disabled={portalLoading}
                className="btn-secondary text-sm whitespace-nowrap"
              >
                {portalLoading ? "Redirecting…" : "Manage subscription →"}
              </button>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="text-xs text-faint hover:text-slate disabled:opacity-50 transition-colors"
              >
                {syncing ? "Syncing…" : "Sync with Stripe"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Plan cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          const isFree = plan.id === "free";
          return (
            <div
              key={plan.id}
              className={`card p-6 flex flex-col ${
                plan.highlighted ? "ring-2 ring-brand-500" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="section-title">{plan.name}</h2>
                {plan.highlighted && !isCurrent && (
                  <span className="text-xs bg-brand-500 text-white px-2 py-0.5 rounded-full font-bold">
                    Most popular
                  </span>
                )}
                {isCurrent && (
                  <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full font-bold">
                    Current
                  </span>
                )}
              </div>
              <p className="text-xs text-muted mt-1">{plan.tagline}</p>

              {isFree ? (
                <p className="text-3xl font-black text-ink mt-3">$0</p>
              ) : (
                <div className="mt-3">
                  <p className="text-3xl font-black text-ink">
                    ${plan.priceAnnual}
                    <span className="text-sm font-bold text-faint">/mo</span>
                  </p>
                  <p className="text-xs text-faint mt-0.5">
                    billed annually · ${plan.priceMonthly}/mo monthly
                  </p>
                </div>
              )}

              <ul className="mt-4 space-y-1.5 text-sm text-slate flex-1">
                {planFeatures(plan).map((f) => (
                  <li key={f}>✓ {f}</li>
                ))}
              </ul>

              {isCurrent ? (
                <button
                  disabled
                  className="btn-secondary w-full mt-4 text-sm opacity-60 cursor-default"
                >
                  Your plan
                </button>
              ) : isFree ? (
                <p className="text-xs text-faint text-center mt-4">
                  Cancel anytime in the billing portal to return to Free.
                </p>
              ) : (
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={!!subscribing}
                  className="btn-primary w-full mt-4 text-sm"
                >
                  {subscribing === plan.id
                    ? "Redirecting…"
                    : onFree
                      ? `Upgrade to ${plan.name}`
                      : `Switch to ${plan.name}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-faint mt-6 text-center">
        Annual billing is discounted. Switch or cancel anytime — no lock-in.
      </p>
    </div>
  );
}
